// # Calendar Heatmap View Component
// Activity heatmap visualization showing event density

'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import type { CalendarEvent } from '../../types/calendar';
import { CalendarEventChip } from './CalendarEventChip';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

interface CalendarHeatmapViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  className?: string;
}

export function CalendarHeatmapView({
  events,
  onEventClick,
  onDateClick,
  onCreateEvent,
  className = ''
}: CalendarHeatmapViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { isMobile } = useDeviceInfo();

  // # Get days for heatmap (3 months)
  const getDaysToDisplay = useCallback((): Date[] => {
    const days: Date[] = [];
    const startDate = new Date(currentDate);
    startDate.setMonth(startDate.getMonth() - 1); // Start 1 month before
    startDate.setDate(1); // First day of month
    
    // Get 3 months of days
    for (let i = 0; i < 90; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [currentDate]);

  // # Format date helper
  const formatDate = useCallback((date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // # Get events for a specific date
  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    if (!events || events.length === 0) return [];
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    
    const dateStr = formatDate(date);
    
    return events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      const normalizedStart = event.start_date.split('T')[0];
      const normalizedEnd = event.end_date.split('T')[0];
      return normalizedStart <= dateStr && normalizedEnd >= dateStr;
    });
  }, [events, formatDate]);

  // # Calculate heat intensity based on event count
  const getHeatIntensity = useCallback((eventCount: number): { color: string; intensity: number } => {
    if (eventCount === 0) {
      return { color: 'bg-gray-100', intensity: 0 };
    } else if (eventCount === 1) {
      return { color: 'bg-blue-200', intensity: 1 };
    } else if (eventCount <= 3) {
      return { color: 'bg-blue-400', intensity: 2 };
    } else if (eventCount <= 5) {
      return { color: 'bg-blue-600', intensity: 3 };
    } else {
      return { color: 'bg-blue-800', intensity: 4 };
    }
  }, []);

  // # Check if date is today
  const isToday = useCallback((date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // # Group days by week for grid layout
  const weeks = useMemo(() => {
    const days = getDaysToDisplay();
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    
    days.forEach((day, index) => {
      if (index === 0 || day.getDay() === 0) {
        if (currentWeek.length > 0) {
          weeks.push(currentWeek);
        }
        currentWeek = [day];
      } else {
        currentWeek.push(day);
      }
    });
    
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks;
  }, [getDaysToDisplay]);

  // # Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 3);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 3);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // # Get current period label
  const getCurrentPeriodLabel = (): string => {
    const days = getDaysToDisplay();
    const start = days[0];
    const end = days[days.length - 1];
    return `${start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  };

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
            {getCurrentPeriodLabel()}
          </h2>
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
            {!isMobile && (
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-200 flex items-center gap-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span>Today</span>
              </button>
            )}
            <div className={`flex items-center bg-gray-100 rounded-md ${isMobile ? 'gap-0 p-0.5' : 'gap-0.5 p-0.5'}`}>
              <button
                onClick={goToPrevious}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Previous"
              >
                <ChevronLeft className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={goToNext}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Next"
              >
                <ChevronRight className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>Less</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-100"></div>
            <div className="w-3 h-3 rounded bg-blue-200"></div>
            <div className="w-3 h-3 rounded bg-blue-400"></div>
            <div className="w-3 h-3 rounded bg-blue-600"></div>
            <div className="w-3 h-3 rounded bg-blue-800"></div>
          </div>
          <span>More</span>
        </div>
      </div>

      {/* Heatmap grid */}
      <div 
        className="flex-1 overflow-auto"
        style={{ 
          touchAction: 'pan-y',
          userSelect: 'none'
        }}
      >
        <div className={`${isMobile ? 'p-2' : 'p-4'}`}>
          {/* Weekday labels */}
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div></div>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className={`text-center font-semibold text-gray-600 ${
                  isMobile ? 'text-[10px]' : 'text-xs'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Heatmap weeks */}
          <div className="space-y-1">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-8 gap-1">
                {/* Week number or month label */}
                <div className={`flex items-center justify-center ${
                  isMobile ? 'text-[10px]' : 'text-xs'
                } text-gray-500 font-medium`}>
                  {week[0] && week[0].getDate() <= 7 && (
                    <span>{week[0].toLocaleDateString('en-US', { month: 'short' })}</span>
                  )}
                </div>

                {/* Days in week */}
                {Array.from({ length: 7 }, (_, dayIndex) => {
                  const date = week[dayIndex];
                  if (!date) {
                    return <div key={dayIndex} className="aspect-square"></div>;
                  }

                  const dateEvents = getEventsForDate(date);
                  const eventCount = dateEvents.length;
                  const heat = getHeatIntensity(eventCount);
                  const isTodayDate = isToday(date);
                  const isSelected = selectedDate && formatDate(selectedDate) === formatDate(date);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                  return (
                    <div
                      key={`${date.toISOString()}-${dayIndex}`}
                      className={`aspect-square rounded cursor-pointer transition-all duration-200 relative group ${
                        isTodayDate
                          ? 'ring-2 ring-blue-500 ring-offset-1'
                          : isSelected
                          ? 'ring-2 ring-blue-300'
                          : ''
                      } ${
                        heat.intensity === 0
                          ? 'bg-gray-100 hover:bg-gray-200'
                          : `${heat.color} hover:opacity-80`
                      }`}
                      onClick={() => {
                        setSelectedDate(date);
                        onDateClick?.(date);
                      }}
                      title={`${date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}: ${eventCount} event${eventCount !== 1 ? 's' : ''}`}
                    >
                      {/* Day number */}
                      <div className={`absolute inset-0 flex items-center justify-center ${
                        isMobile ? 'text-[10px]' : 'text-xs'
                      } ${
                        heat.intensity >= 3
                          ? 'text-white font-bold'
                          : heat.intensity > 0
                          ? 'text-white font-semibold'
                          : 'text-gray-600'
                      }`}>
                        {date.getDate()}
                      </div>

                      {/* Event count badge */}
                      {eventCount > 0 && (
                        <div className={`absolute top-0 right-0 ${
                          isMobile ? 'w-3 h-3' : 'w-4 h-4'
                        } rounded-full bg-white/80 flex items-center justify-center ${
                          isMobile ? 'text-[8px]' : 'text-[10px]'
                        } font-bold text-gray-700`}>
                          {eventCount}
                        </div>
                      )}

                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                          {date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                          {eventCount > 0 && `: ${eventCount} event${eventCount !== 1 ? 's' : ''}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected date events */}
      {selectedDate && (
        <div className={`flex-shrink-0 border-t border-gray-200 ${
          isMobile ? 'p-2' : 'p-4'
        } bg-gray-50 max-h-[200px] overflow-y-auto`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            {onCreateEvent && (
              <button
                onClick={() => onCreateEvent(selectedDate)}
                className={`text-xs font-medium text-blue-600 hover:text-blue-700 ${
                  isMobile ? 'px-2 py-1' : 'px-3 py-1.5'
                }`}
              >
                + Add event
              </button>
            )}
          </div>
          {getEventsForDate(selectedDate).length === 0 ? (
            <p className="text-sm text-gray-500">No events on this date</p>
          ) : (
            <div className="space-y-2">
              {getEventsForDate(selectedDate).map((event) => (
                <CalendarEventChip
                  key={event.id}
                  event={event}
                  onClick={onEventClick}
                  compact={true}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CalendarHeatmapView;

